import { Flex, H2, Input, Skeleton, ToggleButton } from '@dnb/eufemia';
import { Field } from '@dnb/eufemia/extensions/forms';
import { ApiType } from '@portals/shared/portal/ApiDto';
import { AccessTypes } from '@portals/shared/portal/AppDto';
import { useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useBackend } from '@/hooks/useBackend';
import { ApiProviderPageOutletContext } from '@/pages/profile/apps/application/ApiProviderPage';

import AppView from './components/AppView';
import ClientView from './components/ClientView';
import { ClientSource, clientSourceOptions, ViewType } from './components/schemas';

import './toggleButtonAnimation.css';

export function ConsumersTab() {
  const { api, clientSources, setClientSources, ciamClients, ciamClientsLoading, ciamClientsError } =
    useOutletContext<ApiProviderPageOutletContext>();
  const [view, setView] = useState<ViewType>('apps');
  const [animateTab, setAnimateTab] = useState<ViewType | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTabChange = (_view: ViewType) => {
    if (view === _view) return; // No change
    setView(_view);
    setAnimateTab(_view);
    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    animationTimeoutRef.current = setTimeout(() => setAnimateTab(null), 700);
  };

  // Fetch both datasets once, regardless of tab
  const { data: consumerApps, isLoading: appsLoading } = useBackend('/apis/{apiSlug}/apps', {
    params: { path: { apiSlug: api.slug } },
  });
  const { data: clients, isLoading: clientsLoading } = useBackend('/apis/{apiSlug}/clients', {
    params: { path: { apiSlug: api.slug } },
  });

  const [query, setQuery] = useState('');

  const searchField = (
    <Flex.Container align="flex-end" bottom="small" gap="small">
      <Flex.Item grow>
        <Input
          icon="loupe"
          onChange={({ value }) => setQuery(value)}
          placeholder="Search by name, id, description, owner, environment or scopes"
          showClearButton
          stretch
          value={query}
        />
      </Flex.Item>
      {view === 'clients' && (
        <Flex.Item>
          <Field.MultiSelection
            data={clientSourceOptions}
            label="View Clients"
            onChange={(value: ClientSource[]) => {
              setClientSources(value || []);
            }}
            value={clientSources}
          />
        </Flex.Item>
      )}
    </Flex.Container>
  );
  return (
    <Skeleton>
      <Flex.Container align="center" bottom="small" justify="flex-end" top="small">
        <Flex.Item grow>
          <H2>
            {view === 'apps' ? 'Apps' : 'Clients'} with access & pending requests for this{' '}
            {api.type === ApiType.EVENT ? 'event' : api.type === ApiType.MCP ? 'MCP server' : 'API'}
          </H2>
        </Flex.Item>
        {api.accessTypes?.includes(AccessTypes.O_AUTH_SYSTEM_TO_SYSTEM) && (
          <Flex.Item>
            <ToggleButton.Group onChange={(e) => setView(String(e.value) as ViewType)} value={view}>
              <ToggleButton className={animateTab === 'apps' ? 'tab-animate' : ''} text="Apps" value="apps" />
              <ToggleButton className={animateTab === 'clients' ? 'tab-animate' : ''} text="Clients" value="clients" />
            </ToggleButton.Group>
          </Flex.Item>
        )}
      </Flex.Container>
      {searchField}
      {view === 'apps' ? (
        <AppView
          api={api}
          clients={clients}
          consumerApps={consumerApps}
          isLoading={appsLoading || clientsLoading}
          query={query}
          showView={(view, query) => {
            setQuery(query || '');
            handleTabChange(view);
          }}
        />
      ) : (
        <ClientView
          apiSlug={api.slug}
          ciamClients={ciamClients}
          ciamClientsError={ciamClientsError}
          ciamClientsLoading={ciamClientsLoading}
          clientSources={clientSources}
          consumerClients={clients}
          isLoading={clientsLoading}
          query={query}
          showView={(view, query) => {
            setQuery(query || '');
            handleTabChange(view);
          }}
        />
      )}
    </Skeleton>
  );
}
